<template>
  <div class="row-flight" :data-id="flight.id">
    <div class="cell align-left id">
      <div class="full-cell">{{ flight.id.slice(0,6) }}</div>
    </div>
    <div class="cell align-left airline">
      <div class="airline-stack">
        <div class="airline-line airline-line--primary">
          <span class="airline-logo-slot">
            <img
              v-if="airlineLogoUrl(flight.airlineCode)"
              class="airline-logo"
              :src="airlineLogoUrl(flight.airlineCode)"
              :alt="flight.airlineCode + ' logo'"
              loading="lazy"
              @error="onAirlineLogoError"
            />
          </span>
          <EllipsisHoverTooltip
            :text="formatAirlineName(flight.airlineName || flight.airline)"
            label-class="airline-label"
          />
        </div>
        <div v-if="hasCodeshares" class="codeshare-block">
          <div class="codeshare-block__label">{{ $t('airportTable.alsoMarketedAs') }}</div>
          <div
            v-for="(codeshare, index) in flight.codeshares"
            :key="'cs-airline-' + index"
            class="airline-line airline-line--partner"
          >
            <span class="airline-logo-slot airline-logo-slot--partner">
              <img
                v-if="airlineLogoUrl(codeshare.airlineCode)"
                class="airline-logo"
                :src="airlineLogoUrl(codeshare.airlineCode)"
                :alt="codeshare.airlineCode + ' logo'"
                loading="lazy"
                @error="onAirlineLogoError"
              />
            </span>
            <span class="codeshare-flight">{{ formatFlightDesignator(codeshare.airlineCode, codeshare.flightNumber) }}</span>
            <EllipsisHoverTooltip
              :text="codesharePartnerName(codeshare)"
              label-class="airline-label airline-label--partner"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="cell align-center airlineCode">
      <div class="airline-stack airline-stack--codes">
        <div class="stack-line stack-line--primary">{{ flight.airlineCode }}</div>
        <template v-if="hasCodeshares">
          <div class="stack-line stack-line--spacer" aria-hidden="true" />
          <div
            v-for="(codeshare, index) in flight.codeshares"
            :key="'cs-code-' + index"
            class="stack-line stack-line--partner"
          >{{ codeshare.airlineCode }}</div>
        </template>
      </div>
    </div>
    <div class="cell align-center flightNumber">
      <div class="airline-stack airline-stack--codes">
        <div class="stack-line stack-line--primary">{{ flight.flightNumber }}</div>
        <template v-if="hasCodeshares">
          <div class="stack-line stack-line--spacer" aria-hidden="true" />
          <div
            v-for="(codeshare, index) in flight.codeshares"
            :key="'cs-num-' + index"
            class="stack-line stack-line--partner"
          >{{ codeshare.flightNumber }}</div>
        </template>
      </div>
    </div>
    <div class="cell align-center arrival-departure">
      <span class="type-badge" :style="flightTypeBadgeStyle">{{ formatFlightType(flight.flightType) }}</span>
    </div>
    <div class="cell align-left city">
      <div class="city-line">
        <span class="city-flag-slot">
          <img
            v-if="countryFlagUrl"
            class="country-flag"
            :src="countryFlagUrl"
            :alt="countryCode + ' flag'"
            width="28"
            height="21"
            loading="lazy"
            @error="onFlagError"
          />
        </span>
        <span class="city-label">{{ capitalize(flight.airportCity || flight.city) }} ({{ flight.airportCode || flight.cityCode }})</span>
      </div>
    </div>
    <div class="cell align-center sdtDate flex-row">{{ formatDateOnly(flight.scheduled) }}</div>
    <div class="cell align-center scheduled flex-row">{{ formatDate(flight.scheduled) }}</div>
    <div class="cell align-center estimated flex-row">{{ flight.overrides.estimated ? formatDate(flight.overrides.estimated) : formatDate(flight.estimated) }}<span v-if="flight.overrides.estimated" class="modified mdi mdi-flag-outline"></span></div>
    <!--div class="cell align-center">{{ formatDate(flight.actual) }}</div-->
    <div class="cell align-center gate flex-row">{{ flight.overrides.gate ? flight.overrides.gate : flight.gate }}<span v-if="flight.overrides.gate" class="modified mdi mdi-flag-outline"></span></div>
    <div class="cell align-center claim flex-row">{{ flight.overrides.claim ? flight.overrides.claim : flight.claim }}<span v-if="flight.overrides.claim" class="modified mdi mdi-flag-outline"></span></div>
    <div class="cell align-center status flex-row">
      <span
        v-if="mapStatusDisplayName"
        class="status-badge"
        :style="statusBadgeStyle"
      >{{ mapStatusDisplayName }}</span>
      <span v-else class="status-badge" :style="emptyStatusBadgeStyle">—</span>
      <span v-if="flight.overrides.status" class="modified mdi mdi-flag-outline"></span>
    </div>
    <div class="cell align-center">
      <div class="full-cell">{{ flight.overrides.remarks ? flight.overrides.remarks : flight.remarks }}<span v-if="flight.overrides.remarks" class="modified mdi mdi-flag-outline"></span></div>
    </div>
    <div v-if="!this.airline.includes('admin')">
      <div class="cell align-center action" v-if="this.permissions.includes('Edit-Flight-Details') && (this.airline.includes(this.flight.airlineName) || this.airline.includes('admin'))">
        <div class="icon" @click="toggleEditActiveFlyout()"><span class="mdi mdi-pencil"></span></div>
      </div>
      <div class="cell align-center action" v-if="this.permissions.includes('Edit-Flight-Details') && !this.airline.includes(this.flight.airlineName)"></div>
    </div>
    <div v-else>
      <div class="cell align-center action" v-if="this.permissions.includes('Edit-Flight-Details')">
        <div class="icon" @click="toggleEditActiveFlyout()"><span class="mdi mdi-pencil"></span></div>
      </div>
    </div>
    
  </div>

</template>

<script>

import { format } from 'date-fns';
import { decode } from 'html-entities';
import { mapGetters } from 'vuex';
import { getPermissions, getAirlineNames } from '../js/utils/userContext';
import { getFlightTypeBadgeStyle, getStatusBadgeStyle } from '../js/utils/flightStatusStyle';
import { getFlightCountryFlagUrl, resolveFlightCountryCode } from '../js/utils/countryFlag';
import { getNpmAirlineLogoUrl } from '../js/utils/airlineLogo';
import EllipsisHoverTooltip from './EllipsisHoverTooltip.vue';


export default {
  name: 'RowActiveFlight',
  components: {
    EllipsisHoverTooltip
  },
  props:  {
    flight: Object
  },
  computed: {
    ...mapGetters({
      statusByKey: 'airportSettings/statusByKey',
    }),
    permissions() {
      return getPermissions();
    },
    airline() {
      return getAirlineNames();
    },
    flightStatusKey() {
      return this.flight.overrides.status ? this.flight.overrides.status : this.flight.status;
    },
    mapStatusDisplayName() {
      return this.statusByKey[this.flightStatusKey] || '';
    },
    statusBadgeStyle() {
      return getStatusBadgeStyle(this.flightStatusKey);
    },
    emptyStatusBadgeStyle() {
      return getStatusBadgeStyle(null);
    },
    flightTypeBadgeStyle() {
      return getFlightTypeBadgeStyle(this.flight.flightType);
    },
    countryCode() {
      return resolveFlightCountryCode(this.flight);
    },
    countryFlagUrl() {
      return getFlightCountryFlagUrl(this.flight);
    },
    hasCodeshares() {
      return Array.isArray(this.flight.codeshares) && this.flight.codeshares.length > 0;
    }
  },
  methods: {
    airlineLogoUrl(code) {
      return getNpmAirlineLogoUrl(code);
    },
    onFlagError(e) {
      if (e && e.target) e.target.style.visibility = 'hidden';
    },
    onAirlineLogoError(e) {
      if (e && e.target) e.target.style.visibility = 'hidden';
    },
    capitalize(str) {
      return str != null ? decode(str.toLowerCase()) : 'N/A'; // CSS then makes this Capitalize
    },
    formatAirlineName(airlineName) {
      if (airlineName == null || airlineName === '') return '—';
      return String(airlineName).replace(/([A-Z]+)/g, ' $1').replace(/^ /, '');
    },
    formatFlightDesignator(code, number) {
      const c = code != null ? String(code).trim() : '';
      const n = number != null ? String(number).trim() : '';
      if (c && n) return `${c} ${n}`;
      return c || n || '—';
    },
    codesharePartnerName(codeshare) {
      const name = codeshare.airlineName || codeshare.airline;
      if (name) return this.formatAirlineName(name);
      return codeshare.airlineCode || '—';
    },
    formatFlightType(type) {
      if(type.toLowerCase() == 'arrival') return 'A';
      if(type.toLowerCase() == 'departure') return 'D';
      return 'N/A';
    },
    formatDate(date) {
      return format(new Date(date), 'HH:mm');
    },
    formatDateOnly(date) {
      try {
        if (!date) return '—';
        return format(new Date(date), 'yyyy-MM-dd');
      } catch (e) {
        return '—';
      }
    },
    toggleEditActiveFlyout() {
      this.$emit('toggleEditActiveFlyout', this.flight);
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  .row-flight {
    width: 100%;
    height: auto;
    min-height: 50px;
    position: relative;
    float: left;
    display: flex;
    background-color: #fff;
    border-bottom: 1px solid hsl(211, 30%, 92%);
    transition: background-color 0.15s ease;

    &:hover {
      background-color: hsl(211, 70%, 94%);
      z-index: 3;
    }

    .cell {
      height: 100%;
      padding: 15px;
      position: relative;
      float: left;
      color: $general-black-light;
      font-size: 12px;
      line-height: 1.8em;
      flex-basis: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      flex-direction: column;

      &.flex-row {
        flex-direction: row;
      }

      &.id {
        min-width: 80px;
        max-width: 80px;
      }

      &.airline {
        min-width: 240px;
        max-width: 240px;
        text-transform: capitalize;
        align-items: flex-start;
        justify-content: center;
        overflow: visible;

        .airline-stack {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .airline-line {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: flex-start;
          width: 100%;
          gap: 8px;
          min-height: 32px;

          &--partner {
            min-height: 26px;
            padding-left: 4px;
            border-left: 2px solid rgba($brand-primary, 0.2);
          }
        }

        .codeshare-block {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed hsl(211, 30%, 86%);
          display: flex;
          flex-direction: column;
          gap: 6px;

          &__label {
            font-size: 10px;
            font-weight: 700;
            line-height: 1.2;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: rgba($brand-primary, 0.55);
            padding-left: 4px;
          }
        }

        .codeshare-flight {
          flex-shrink: 0;
          font-size: 11px;
          font-weight: 700;
          line-height: 1.2;
          color: $brand-primary;
          white-space: nowrap;
        }

        .airline-logo-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 44px;
          min-width: 44px;
          height: 32px;
          padding: 3px 4px;
          box-sizing: border-box;
          border-radius: 6px;
          overflow: hidden;
          line-height: 0;
          background: $general-white;
          border: 1px solid hsl(211, 30%, 88%);
          box-shadow: 0 1px 2px rgba($brand-primary-dark, 0.08);

          &--partner {
            width: 36px;
            min-width: 36px;
            height: 26px;
            padding: 2px 3px;
          }
        }

        .airline-logo {
          display: block;
          width: 100%;
          height: 100%;
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          object-position: center center;
        }

        ::v-deep .airline-label {
          &--partner {
            font-size: 11px;
            font-weight: 500;
            color: rgba($brand-primary-dark, 0.88);
          }
        }
      }

      &.airlineCode,
      &.flightNumber {
        .airline-stack--codes {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .stack-line {
          width: 100%;
          text-align: center;
          line-height: 1.2;

          &--primary {
            min-height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          &--spacer {
            min-height: 34px;
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px dashed hsl(211, 30%, 86%);
            box-sizing: border-box;
          }

          &--partner {
            min-height: 26px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: 600;
            color: rgba($brand-primary, 0.75);
          }
        }
      }

      &.arrival-departure {
        max-width: 60px;

        .type-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 28px;
          height: 28px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 11px;
          line-height: 1;
          box-sizing: border-box;
        }
      }

      &.airlineCode, &.flightNumber, &.scheduled, &.estimated, &.gate, &.claim, &.action {
        min-width: 80px;
        max-width: 120px;
      }

      &.sdtDate {
        min-width: 104px;
        max-width: 104px;
        white-space: nowrap;
      }

      &.city {
        min-width: 200px;
        max-width: 200px;
        text-transform: capitalize;
        line-height: 21px;
        justify-content: center;
        align-items: flex-start;

        .city-line {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: flex-start;
          width: 100%;
          gap: 10px;
          min-height: 21px;
          line-height: 21px;
        }

        .city-flag-slot {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-shrink: 0;
          width: 28px;
          min-width: 28px;
          height: 21px;
          line-height: 0;
        }

        .country-flag {
          display: block;
          width: 28px;
          height: 21px;
          margin: 0;
          object-fit: cover;
          border-radius: 2px;
          box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
        }

        .city-label {
          flex: 1 1 auto;
          min-width: 0;
          line-height: 21px;
          padding: 0;
          text-align: left;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      &.status {
        min-width: 140px;
        max-width: 140px;
        gap: 4px;

        .status-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 100px;
          height: 28px;
          min-width: 100px;
          max-width: 100px;
          padding: 0 6px;
          box-sizing: border-box;
          border-radius: 6px;
          font-weight: 700;
          font-size: 11px;
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .modified {
        color: $brand-primary;
        margin-left: 2px;
      }

      .full-cell {
        width: 100%;
        position: relative;

      }

      .icon {
        width: 36px;
        height: 36px;
        font-size: 18px;
        border-radius: 8px;
        background: hsl(211, 70%, 95%);
        color: $brand-primary-dark;
        cursor: pointer;
        transition: background-color 0.15s, color 0.15s;

        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: $brand-primary;
          color: $general-white;
        }
      }

      &.align-left {
        justify-content: flex-start;
        text-align: left;
      }

      &.align-center {
        justify-content: center;
        text-align: center;
      }

      &.align-right {
        justify-content: flex-end;
        text-align: right;
      }

    }
  }

  /* Responsive tweaks to ensure row fits on laptop screens without zoom */
  @media screen and (max-width: 1600px) {
    .row-flight {
      .cell {
        font-size: 11px;
        padding: 12px;
        &.id { min-width: 70px; max-width: 70px; }
        &.airline { min-width: 220px; max-width: 220px; }
        &.city { min-width: 190px; max-width: 190px; }
        &.airlineCode, &.flightNumber, &.scheduled, &.estimated, &.gate, &.claim, &.action {
          min-width: 75px; max-width: 110px;
        }
        &.sdtDate { min-width: 100px; max-width: 100px; }
        &.status {
          min-width: 130px;
          max-width: 130px;

          .status-badge {
            width: 92px;
            min-width: 92px;
            max-width: 92px;
          }
        }
      }
    }
  }

  @media screen and (max-width: 1366px) {
    .row-flight {
      .cell {
        font-size: 10px;
        padding: 10px;
        &.id { min-width: 64px; max-width: 64px; }
        &.airline { min-width: 200px; max-width: 200px; }
        &.city { min-width: 170px; max-width: 170px; }
        &.airlineCode, &.flightNumber, &.scheduled, &.estimated, &.gate, &.claim, &.action {
          min-width: 68px; max-width: 100px;
        }
        &.sdtDate { min-width: 96px; max-width: 96px; }
        &.status {
          min-width: 120px;
          max-width: 120px;

          .status-badge {
            width: 84px;
            min-width: 84px;
            max-width: 84px;
            font-size: 10px;
          }
        }
      }
    }
  }
</style>
